/**
 * Opaque bearer tokens: API keys, cart tokens, customer sessions.
 *
 * All three are the same object -- a high-entropy random string the client
 * presents verbatim and the server recognises by its digest -- so they share
 * one mint and one hash. Only the SHA-256 ever touches storage (Postgres for
 * the row, KV for an API key's verification entry); the plaintext is shown or
 * set as a cookie exactly once and is never logged.
 *
 * SHA-256 rather than bcrypt on purpose. A password is low-entropy and guessed
 * offline, so it needs a slow hash. These are 256 random bits, which no amount
 * of hashing speed makes guessable, and the lookup runs on every request --
 * a slow hash here would be a per-request tax that buys nothing.
 */

const encoder = new TextEncoder();

function toHex(bytes: ArrayBuffer | Uint8Array): string {
  return Array.from(new Uint8Array(bytes))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Hex SHA-256 of a presented token. Deterministic, so it doubles as the lookup key. */
export async function hashToken(raw: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(raw));

  return toHex(digest);
}

/**
 * A fresh token: 32 random bytes as 64 hex characters, under an optional prefix.
 *
 * The prefix (`ctrl_sk_` for API keys) is for humans and secret scanners, not
 * for the server -- it identifies what kind of credential leaked, and lets a
 * list show which key is which without exposing the key.
 */
export function mintToken(prefix = ''): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));

  return `${prefix}${toHex(bytes)}`;
}

/** Shape check for a token we minted: hex body of the expected length. */
export function looksLikeToken(raw: string, prefix = ''): boolean {
  if (!raw.startsWith(prefix)) return false;

  return /^[a-f0-9]{64}$/.test(raw.slice(prefix.length));
}
